/**
 * TemplateDetail.tsx — 推送模板详情/编辑弹窗（注册名 template-detail）
 *
 * 移植自原型 design/Novel-admin/src/dialogs2.jsx 1564–1904。
 * view / edit 双模式：仅「内容」（多语言标题+正文 + 变量插入工具条 + 可折叠客户端预览）。
 * F4：原型的「使用记录」tab + 头部「累计发送 / 平均打开率 / 本周触发」KPI 全为合成数字（无契约统计端点）→ 移除，
 *      只渲染真值「多语言 N/7」（派生自已维护 i18n 键）。接入推送统计端点后再补真值（记 docs/plans §五）。
 * 机械转换：
 *   - JS→TS（受控 props + 严格类型）；
 *   - window.__multiLang.useMultiLang/MultiLangFormRows → 本地受控 state + ~/ui/MultiLangFormRows（受控 value/onChange）；
 *   - window.SYS_CFG.SYSTEM_LANGUAGES → useSysConfig() 的 languages（取 enabled，display_name_i18n.zh 作展示名）；
 *   - window.toast → ~/ui/Toast 的 toast()；DialogShell → ~/ui/Dialog 的 Dialog 壳；
 *   - Stat → 共享 ~/ui/StatCard（对齐原型 dialogs2.jsx 的 UI.Stat，替代 push 域本地副本）。
 * 变量插入沿用原型 DOM focus 追踪（lastFieldRef）：MultiLangFormRows 渲染真实 input/textarea，
 * 用原生 value setter 写入并派发 input 事件让受控 onChange 生效。
 * JSX/className/inline-style 与原型逐字一致。
 */

import { PUSH_TEMPLATE_REGISTRY, type UpsertPushTemplateRequest } from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { AdminApiError } from '~/api/errors';
import type { PushTemplate } from '~/data/queries';
import { useSysConfig } from '~/data/queries';
import { pushApi } from '~/hooks/use-push';
import { Icons } from '~/lib/icons';
import { Dialog } from '~/ui/Dialog';
import { MultiLangFormRows } from '~/ui/MultiLangFormRows';
import { Stat } from '~/ui/StatCard';
import { toast } from '~/ui/Toast';

/** 确认快照来源模板（A4 §1 content_source=CONFIRMED_MESSAGE）的编辑入口禁用说明 */
export const READ_ONLY_TEMPLATE_TITLE = '只读 · 该模板内容按单用户逐条确认，不在模板页维护';

export function TemplateDetail({
  tpl,
  edit,
  canWrite,
  onClose,
}: {
  tpl: PushTemplate;
  edit?: boolean;
  canWrite: boolean;
  onClose: () => void;
}) {
  const t = tpl;
  const availableVariables = (PUSH_TEMPLATE_REGISTRY[t.slug]?.placeholders ?? []).map(
    (name) => `{${name}}`,
  );
  const { data: sys } = useSysConfig();
  const queryClient = useQueryClient();
  // enabled 语言（带中文展示名），用于 view 模式逐语言展示 + 预览语言切换
  const enabledLangs = sys.languages
    .filter((l) => l.enabled)
    .map((l) => ({
      c: l.language_code,
      zh: (l.display_name_i18n.zh as string) ?? l.language_code,
    }));
  const enabledLangCount = enabledLangs.length;

  // 确认快照来源的模板行只读（A4 §1 / §2.2），即使从「编辑」入口打开也停在查看态
  const [mode, setMode] = useState<'view' | 'edit'>(edit && !t.readOnly ? 'edit' : 'view');
  // 预览面板（默认折叠 · 点击展开）
  const [showPreview, setShowPreview] = useState(false);
  // 预览语言（仅影响右侧预览 + view 模式下显示哪种语言）
  const [previewLang, setPreviewLang] = useState('zh');

  // 多语言编辑态（zh/en 必填 + 已维护的其他语言）— 受控 state，初值由模板**真实 i18n**派生。
  // 接真：title_i18n / body_i18n 来自后端 DTO（adapter 透传），upsert 保存按 slug 回传全量 map。
  // 仍兜底补 zh/en 空键，保证多语言录入组件渲染中英两行（契约：title/body 的 en 必填非空）。
  const buildInitial = (src: Record<string, string>): Record<string, string> => {
    const init: Record<string, string> = { ...src };
    if (init.zh === undefined) init.zh = '';
    if (init.en === undefined) init.en = '';
    return init;
  };
  const [titleVals, setTitleVals] = useState<Record<string, string>>(() =>
    buildInitial(t.titleI18n),
  );
  const [bodyVals, setBodyVals] = useState<Record<string, string>>(() => buildInitial(t.bodyI18n));
  // 已存 locale 即使当前被 system-config 禁用也必须继续展示，否则 service 校验 merge 后 map 时，
  // 运营无法修正历史遗留的非法占位符，模板会陷入所有更新都被拒的不可修复状态。
  const editableLanguageCodes = Array.from(
    new Set([
      ...enabledLangs.map((language) => language.c),
      ...Object.keys(titleVals),
      ...Object.keys(bodyVals),
    ]),
  );

  // 用于预览的值：优先当前编辑值，未维护时 fallback 英文
  const maintainedLangs = Object.keys(titleVals).filter((k) => titleVals[k]);
  const previewTitle = titleVals[previewLang] || titleVals.en || '';
  const previewBody = bodyVals[previewLang] || bodyVals.en || '';
  const previewIsFallback = !titleVals[previewLang] || !bodyVals[previewLang];

  // 记录最后聚焦的标题/正文输入框，供变量插入定位（点击芯片不会丢失输入框焦点）
  const lastFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const insertVar = (v: string) => {
    const el = lastFieldRef.current;
    if (!el || !document.body.contains(el)) {
      toast(`请先点击标题或正文输入框，再插入 ${v}`, { tone: 'warn' });
      return;
    }
    const s = el.selectionStart ?? el.value.length;
    const e = el.selectionEnd ?? el.value.length;
    const next = el.value.slice(0, s) + v + el.value.slice(e);
    // 经原生 value setter 写入，再派发 input 事件 → 受控 MultiLangFormRows 的 onChange 生效
    const proto =
      el instanceof HTMLTextAreaElement
        ? HTMLTextAreaElement.prototype
        : HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
    setter?.call(el, next);
    const pos = s + v.length;
    el.focus();
    el.setSelectionRange(pos, pos);
    el.dispatchEvent(new Event('input', { bubbles: true }));
    toast(`已插入 ${v}`, { tone: 'default' });
  };
  // 在编辑区捕获 focusin，记住当前输入框
  const trackFocus = (e: React.FocusEvent) => {
    const el = e.target as HTMLElement;
    if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) {
      lastFieldRef.current = el as HTMLInputElement | HTMLTextAreaElement;
    }
  };

  // 模板保存（ADM-PUSH-S002 · PUT /push/templates）：按 slug 回传全量 title/body i18n + 启停。
  // 契约：title/body 的 en 必填非空（PushTemplateI18nSchema）；slug ∈ §2.5.2 baseline 闭集；功能类禁停由 server guard。
  const saveMutation = useMutation({
    mutationFn: (body: UpsertPushTemplateRequest) => pushApi.upsertTemplate(body),
    onSuccess: async () => {
      toast(`已保存模板「${t.type}」`, { tone: 'pos', icon: 'IconCheck' });
      // upsert 后失效模板列表 → 卡片/详情重拉服务端真值（含 merge 保留的其他 locale）
      await queryClient.invalidateQueries({ queryKey: ['push-templates'] });
      setMode('view');
    },
    onError: (err) => {
      // AdminApiError.message 已按 code 本地化；失败不切回 view、不丢编辑内容（草稿保留）
      toast(err instanceof AdminApiError ? err.message : '保存模板失败', {
        tone: 'neg',
        icon: 'IconAlert',
      });
    },
  });

  // en 必填非空守卫（契约 PushTemplateI18nSchema：title/body 的 en 必填）→ 前端先拦，给清晰原因
  const enReady = (titleVals.en ?? '').trim().length > 0 && (bodyVals.en ?? '').trim().length > 0;
  const saveDisabled = !canWrite || !enReady || saveMutation.isPending;
  const saveTitle = !canWrite
    ? '权限不足 · 该操作受角色限制（后端 requireRole 为权威）'
    : !enReady
      ? '标题与正文的英文（EN）版本必填且非空'
      : '';
  // F5：剥除空的 optional locale 键再持久化。buildInitial 给缺失 zh/en 注入空串供编辑器渲染中英两行，
  // 这些合成的空键不能上送（en-only 模板打开未改保存不应凭空写出 `zh:""`）。
  // 但运营把一个**原本有值**的非 en 语言清空时要原样以空串上送：服务端据此把该语言单元重新排队、
  // 由自动补齐重新译出（`86-运营配置自动翻译.md` §1.7）；只剥掉的话 merge 会保留旧值，清空无效。
  const stripEmptyLocales = (
    m: Record<string, string>,
    original: Record<string, string> | undefined,
  ): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(m)) {
      if (typeof v !== 'string') continue;
      if (v.trim().length > 0) out[k] = v;
      else if (k !== 'en' && (original?.[k] ?? '').trim().length > 0) out[k] = '';
    }
    return out;
  };
  const onSave = () => {
    if (saveDisabled) return;
    // 本弹窗只编辑文案（title/body i18n），无 active 启停控件。
    // is_active 省略上送（schema .optional）→ server 保留该行原启停态，避免用打开弹窗时抓拍的
    //   stale t.isActive 覆写他人并发 toggle（隐式回滚启停事故）。启停走独立入口，不随文案保存。
    saveMutation.mutate({
      template_slug: t.slug,
      title_i18n: stripEmptyLocales(titleVals, t.titleI18n),
      body_i18n: stripEmptyLocales(bodyVals, t.bodyI18n),
    });
  };

  return (
    <Dialog
      width={820}
      title={mode === 'edit' ? `编辑模板 · ${t.type}` : `推送模板 · ${t.type}`}
      subtitle={`${t.id} · ${t.category}类推送${t.category === '营销' ? '（受营销开关影响）' : '（不受营销开关影响）'}`}
      icon={t.category === '营销' ? 'IconFlame' : 'IconBell'}
      onClose={onClose}
      footer={
        mode === 'view' ? (
          <>
            {/* F4：累计发送 / 平均打开率无契约统计端点 → 不显杜撰数字，仅留 slug 标识 */}
            <span className="mono" style={{ flex: 1, fontSize: 11, color: 'var(--fg-faint)' }}>
              {t.slug}
            </span>
            <button className="btn" onClick={onClose}>
              关闭
            </button>
            {/* 测试发送：无 test-send 端点 → NOT-in-scope（§五）*/}
            <button
              className="btn"
              disabled
              title="NOT-in-scope · 契约无对应端点（见 docs/plans/2026-06-12-web-real-api-integration-plan §五）"
            >
              <Icons.IconPaperPlane size={11} />
              测试发送
            </button>
            <button
              className="btn btn-accent"
              disabled={!canWrite || t.readOnly}
              title={
                t.readOnly
                  ? READ_ONLY_TEMPLATE_TITLE
                  : canWrite
                    ? ''
                    : '权限不足 · 该操作受角色限制（后端 requireRole 为权威）'
              }
              onClick={() => setMode('edit')}
            >
              <Icons.IconEdit size={11} />
              编辑
            </button>
          </>
        ) : (
          <>
            <span style={{ flex: 1, fontSize: 11, color: 'var(--fg-muted)' }}>
              中英必填 · 其他语言未维护时 fallback 英文
            </span>
            <button
              className="btn"
              onClick={() => setMode('view')}
              disabled={saveMutation.isPending}
            >
              取消
            </button>
            {/* 保存草稿：push_templates 无草稿态（server 直写正式行）→ NOT-in-scope（§五）*/}
            <button
              className="btn"
              disabled
              title="NOT-in-scope · 契约无对应端点（见 docs/plans/2026-06-12-web-real-api-integration-plan §五）"
            >
              保存草稿
            </button>
            <button
              className="btn btn-accent"
              disabled={saveDisabled}
              title={saveTitle}
              onClick={onSave}
            >
              <Icons.IconCheck size={11} />
              保存
            </button>
          </>
        )
      }
    >
      {/* Header KPIs — F4：仅保留真值「多语言 N/7」（派生自已维护 i18n 键）；
          累计发送 / 平均打开率 / 本周触发无契约统计端点 → 移除，不显杜撰数字 */}
      <div
        style={{
          padding: 18,
          borderBottom: '1px solid var(--border)',
        }}
      >
        <Stat
          label="多语言"
          v={
            <span>
              <span className="mono strong">{maintainedLangs.length}</span>{' '}
              <span style={{ color: 'var(--fg-faint)' }}>/ {enabledLangCount}</span>
            </span>
          }
        />
      </div>

      {/* F4：移除「使用记录」tab（合成触发记录无统计端点）；仅保留「内容」，去掉单 tab 切换条 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: showPreview ? 'minmax(0, 1.4fr) minmax(0, 1fr)' : '1fr',
          gap: 0,
          borderBottom: '1px solid var(--border)',
          transition: 'grid-template-columns 0.2s',
        }}
      >
        {/* LEFT — editor (view: 摘要展示 / edit: 多语言组件) */}
        <div
          style={{
            padding: '22px 24px',
            borderRight: showPreview ? '1px solid var(--border)' : 0,
          }}
        >
          <div className="hstack" style={{ marginBottom: 16 }}>
            <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
              {mode === 'edit'
                ? '中英必填 · 其他语言可手动添加 · 未维护时 fallback 英文'
                : `已维护 ${maintainedLangs.length} / ${enabledLangCount} 种语言`}
            </span>
            <div style={{ flex: 1 }} />
            <button
              className={`btn btn-sm ${showPreview ? 'btn-accent' : ''}`}
              onClick={() => setShowPreview((v) => !v)}
            >
              <Icons.IconEye size={11} />
              {showPreview ? '收起预览' : '客户端预览'}
            </button>
          </div>

          {mode === 'edit' ? (
            <div className="vstack" style={{ gap: 24 }} onFocusCapture={trackFocus}>
              {/* Sticky variable toolbar — always visible while editing */}
              <div
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 5,
                  background: 'var(--bg-elev)',
                  margin: '-22px -24px 0',
                  padding: '12px 24px',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: 12 }}>可用变量</span>
                  <span style={{ fontSize: 10.5, color: 'var(--fg-muted)' }}>
                    点击插入到光标位置
                  </span>
                  <div style={{ flex: 1 }} />
                  {availableVariables.map((v) => (
                    <button
                      key={v}
                      className="chip mono"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => insertVar(v)}
                      style={{
                        fontSize: 11,
                        cursor: 'pointer',
                        padding: '3px 8px',
                        border: '1px dashed var(--border)',
                        background: 'var(--bg-subtle)',
                      }}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="hstack" style={{ marginBottom: 8, gap: 6 }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>标题模板</span>
                  <span className="chip" style={{ fontSize: 9.5, padding: '0 5px' }}>
                    必填
                  </span>
                </div>
                <MultiLangFormRows
                  label="标题"
                  value={titleVals}
                  onChange={setTitleVals}
                  // 「其他语言」全集 = useSysConfig enabled 语言 code（组件默认 fallback 仅 zh/en，不读 mock seed）
                  availableLangs={editableLanguageCodes}
                  placeholderMap={{
                    zh: '推送标题…',
                    en: 'Push title…',
                  }}
                  placeholderTpl={(zh) => `${zh}标题…`}
                />
              </div>

              <div>
                <div className="hstack" style={{ marginBottom: 8, gap: 6 }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>正文模板</span>
                  <span className="chip" style={{ fontSize: 9.5, padding: '0 5px' }}>
                    可空
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>未填则仅显示标题</span>
                </div>
                <MultiLangFormRows
                  label="正文"
                  value={bodyVals}
                  onChange={setBodyVals}
                  multiline
                  // 「其他语言」全集 = useSysConfig enabled 语言 code（组件默认 fallback 仅 zh/en，不读 mock seed）
                  availableLangs={editableLanguageCodes}
                  placeholderMap={{ zh: '正文…', en: 'Body…' }}
                  placeholderTpl={(zh) => `${zh}正文…`}
                />
              </div>
            </div>
          ) : (
            <div className="vstack" style={{ gap: 20 }}>
              <div>
                <div className="hstack" style={{ marginBottom: 8, gap: 6 }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>标题模板</span>
                  <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                    · 已维护 {Object.values(titleVals).filter((v) => v).length} / {enabledLangCount}
                  </span>
                </div>
                <div className="vstack" style={{ gap: 6 }}>
                  {enabledLangs
                    .filter((l) => titleVals[l.c] !== undefined)
                    .map((l) => (
                      <div
                        key={l.c}
                        className="hstack"
                        style={{
                          gap: 8,
                          alignItems: 'flex-start',
                          padding: '6px 0',
                          borderBottom: '1px dashed var(--border)',
                        }}
                      >
                        <span
                          className="chip"
                          style={{ fontSize: 10, padding: '0 6px', flexShrink: 0 }}
                        >
                          <span className="mono">{l.c.toUpperCase()}</span>
                          <span style={{ color: 'var(--fg-faint)', marginLeft: 3 }}>{l.zh}</span>
                        </span>
                        <span style={{ fontSize: 12.5, color: 'var(--fg)', lineHeight: 1.55 }}>
                          {titleVals[l.c] || (
                            <span style={{ color: 'var(--fg-faint)', fontStyle: 'italic' }}>
                              未维护 · fallback EN
                            </span>
                          )}
                        </span>
                      </div>
                    ))}
                </div>
              </div>

              <div>
                <div className="hstack" style={{ marginBottom: 8, gap: 6 }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>正文模板</span>
                  <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                    · 已维护 {Object.values(bodyVals).filter((v) => v).length} / {enabledLangCount}
                  </span>
                </div>
                <div className="vstack" style={{ gap: 6 }}>
                  {enabledLangs
                    .filter((l) => bodyVals[l.c] !== undefined)
                    .map((l) => (
                      <div
                        key={l.c}
                        className="hstack"
                        style={{
                          gap: 8,
                          alignItems: 'flex-start',
                          padding: '6px 0',
                          borderBottom: '1px dashed var(--border)',
                        }}
                      >
                        <span
                          className="chip"
                          style={{ fontSize: 10, padding: '0 6px', flexShrink: 0 }}
                        >
                          <span className="mono">{l.c.toUpperCase()}</span>
                          <span style={{ color: 'var(--fg-faint)', marginLeft: 3 }}>{l.zh}</span>
                        </span>
                        <span style={{ fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.55 }}>
                          {bodyVals[l.c] || (
                            <span style={{ color: 'var(--fg-faint)', fontStyle: 'italic' }}>
                              未维护
                            </span>
                          )}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT — preview (collapsible) */}
        {showPreview && (
          <div style={{ padding: '22px 20px', background: 'var(--bg-subtle)' }}>
            <div className="hstack" style={{ marginBottom: 10, gap: 6 }}>
              <span style={{ fontWeight: 500, fontSize: 12.5 }}>客户端预览</span>
              <div style={{ flex: 1 }} />
              <button
                className="btn btn-ghost btn-icon"
                style={{ width: 22, height: 22 }}
                onClick={() => setShowPreview(false)}
                title="收起预览"
              >
                <Icons.IconX size={11} />
              </button>
            </div>
            {/* Preview language switcher */}
            <div className="hstack" style={{ gap: 4, marginBottom: 12, flexWrap: 'wrap' }}>
              {enabledLangs
                .filter((l) => titleVals[l.c] !== undefined || l.c === 'zh' || l.c === 'en')
                .map((l) => (
                  <button
                    key={l.c}
                    onClick={() => setPreviewLang(l.c)}
                    className="focusable"
                    style={{
                      padding: '2px 7px',
                      borderRadius: 4,
                      border:
                        previewLang === l.c ? '1px solid var(--accent)' : '1px solid var(--border)',
                      background: previewLang === l.c ? 'var(--accent-soft)' : 'var(--bg-elev)',
                      color: previewLang === l.c ? 'var(--accent-fg)' : 'var(--fg-muted)',
                      font: 'inherit',
                      fontSize: 10.5,
                      cursor: 'pointer',
                    }}
                  >
                    <span className="mono">{l.c.toUpperCase()}</span>
                    <span
                      style={{
                        marginLeft: 3,
                        color: previewLang === l.c ? 'inherit' : 'var(--fg-faint)',
                      }}
                    >
                      {l.zh}
                    </span>
                  </button>
                ))}
            </div>
            <div style={{ marginBottom: 14 }}>
              <div className="card" style={{ padding: 12, background: 'var(--bg-elev)' }}>
                <div className="hstack" style={{ gap: 9, marginBottom: 6 }}>
                  <div
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 5,
                      background: 'var(--fg-strong)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--bg)',
                    }}
                  >
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <path d="M5 4v16M5 4l14 16M19 4v16" />
                    </svg>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--fg-strong)' }}>
                    Rivmoon
                  </span>
                  <div style={{ flex: 1 }} />
                  <span className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)' }}>
                    now
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--fg-strong)',
                    lineHeight: 1.35,
                    marginBottom: 4,
                  }}
                >
                  {previewTitle}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', lineHeight: 1.5 }}>
                  {previewBody}
                </div>
              </div>
              <div className="hstack" style={{ marginTop: 6, gap: 6 }}>
                {previewIsFallback && (
                  <span className="chip chip-warn" style={{ fontSize: 9.5 }}>
                    <Icons.IconAlert size={9} />
                    {previewLang.toUpperCase()} 未维护 · fallback EN
                  </span>
                )}
                <div style={{ flex: 1 }} />
                <span style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>iOS 锁屏样式</span>
              </div>
            </div>

            <div className="label" style={{ marginBottom: 6 }}>
              测试发送
            </div>
            <div className="hstack" style={{ gap: 6 }}>
              <input
                className="input mono"
                placeholder="user_id / 邮箱"
                style={{ flex: 1, fontSize: 11.5 }}
                disabled
              />
              {/* 测试发送：无 test-send 端点 → NOT-in-scope（§五）*/}
              <button
                className="btn btn-sm"
                disabled
                title="NOT-in-scope · 契约无对应端点（见 docs/plans/2026-06-12-web-real-api-integration-plan §五）"
              >
                <Icons.IconPaperPlane size={11} />
                发送
              </button>
            </div>
            <div style={{ marginTop: 4, fontSize: 10.5, color: 'var(--fg-faint)' }}>
              仅发送给运营白名单设备
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}
